import {Component, Prop} from "vue-facing-decorator";
import RouteComponent from "@/components/shared/RouteComponent";
import {EnhancedWorkpace, WorkspaceRole} from "@/types/Workspace";
import {NotUniqueError} from "@/errors/errors";
import Idea, {itemDeletable, itemEditable, ItemAccess} from "@/types/Idea";
import Workflow from "@/types/Workflow";
import Domain from "@/types/Domain";
import IdeaProperty from "@/types/IdeaProperty";
import {PropertySave} from "@/types/PropertySave";
import {AccessLevel} from "@/types/AccessLevel";
import {canCreateIn} from "@/utils/instanceCreation";
import {refuse} from "@/utils/refusal";

/**
 * A chave com que as pessoas do ambiente descem a todo componente — o valor de usuário desenhado na
 * ficha, na célula, no card, e o campo que escolhe. Quem oferece é a raiz (`App.vue`, o `provide`), e
 * quem as lê declara `@Inject({from: WORKSPACE_PEOPLE, default: () => []})`: fora de um ambiente (o
 * laboratório do Kanban), não há pessoas, e o valor de usuário sai vazio.
 *
 * Por `provide`, e não como prop: os domínios descem à mão (`choice-domains`) por doze componentes até
 * o `PropertyValue`, e as pessoas seriam a segunda lista pelo mesmo caminho.
 */
export const WORKSPACE_PEOPLE = 'workspacePeople'

/**
 * A chave com que o AMBIENTE desce, pelo mesmo caminho das pessoas: o markdown que cita itens, coleções
 * e pessoas (`markdownMentions`) precisa do catálogo, das pessoas e do alias do ambiente onde quer que
 * seja desenhado. Quem o lê declara `@Inject({from: WORKSPACE, default: null})`.
 */
export const WORKSPACE = 'workspace'

@Component
class WorkspaceRouteComponent extends RouteComponent {
    @Prop({required: true}) readonly workspace!: EnhancedWorkpace;

    /**
     * Pode alterar, exigindo este nível? É o que decide se um botão de alteração é DESENHADO.
     *
     * Aqui, e não repetido em cada tela, por dois motivos. O primeiro é o de sempre: a mesma
     * pergunta em vinte lugares divergiria — bastaria uma tela perguntar `workspace.closed`
     * com o sinal trocado. O segundo é o alcance: toda tela de ambiente desce desta base, então
     * um método aqui chega a todas sem nenhum encanamento.
     *
     * **Cada botão diz o que exige**: `can('designer')` no que mexe no modelo, `can('editor')`
     * no que mexe nos dados, `can('admin')` no que mexe no ambiente. Era um `canWrite` sem
     * argumento, de quando a única pergunta era o encerramento; o nome saiu junto de propósito,
     * porque um `v-if="canWrite"` esquecido num template continuaria compilando — método é
     * sempre verdadeiro — e o botão apareceria para quem não pode.
     *
     * **Desenhar ou não, e não habilitar ou não.** Botão cinza convida ao clique e pede uma
     * explicação em cima dele; botão ausente não pede nada — e é por isso que o único aviso de
     * ambiente encerrado é o selo da barra.
     *
     * Isto é o que a TELA mostra. Quem garante é o servidor, no portão do `systemAuth`: um
     * pacote antigo em cache ainda desenha os botões, e lá eles são recusados.
     */
    can(level: AccessLevel): boolean {
        return this.workspace.can(level)
    }

    /** O nível basta, sem olhar o encerramento. É o que reabrir precisa — ver o `Workspace`. */
    is(level: AccessLevel): boolean {
        return this.workspace.is(level)
    }

    /**
     * Pode editar ESTE item? O nível e o ambiente (`can('editor')`), e o acesso que a fórmula da coleção
     * deu a quem olha (`_access`, que a leitura traz). Sem `_access`, não há fórmula — pode. Um dono
     * para a ficha, a listagem e o Kanban, que desenham o botão e o arrasto.
     */
    canEditItem(item: { _access?: ItemAccess }): boolean {
        return this.can('editor') && itemEditable(item)
    }

    /** Pode excluir ESTE item? Como o `canEditItem`, pela resposta do `deletable`. */
    canDeleteItem(item: { _access?: ItemAccess }): boolean {
        return this.can('editor') && itemDeletable(item)
    }

    /** Pode criar nesta coleção? A regra mora no `instanceCreation`, que a barra do casco também lê. */
    canCreateIn(idea: Idea | undefined): boolean {
        return canCreateIn(this.workspace, idea)
    }

    /**
     * Encerra ou reabre o ambiente, e manda relê-lo.
     *
     * Aqui, e não na única tela que o oferece, porque é onde moram as outras escritas do
     * AMBIENTE — criar e apagar coleção, fluxo e domínio. São dois donos, cada um do seu
     * pedaço: o `$systems.setClosed` sabe o corpo exato que o servidor aceita, e isto sabe o
     * que vem em volta.
     *
     * O `updateSystem` é o que faz o `App.vue` reler, e ele é indispensável: o encerramento muda
     * o que TODA tela desenha — o selo da barra, os botões, o que as rotas aceitam. Guardar a
     * mudança só na tela que a pediu deixaria o resto do app achando que nada mudou.
     */
    async setWorkspaceClosed(closed: boolean) {
        await this.saving(async () => {
            await this.$systems.setClosed({systemAlias: this.workspace.alias}, closed)
            this.info(this.$tt(closed ? 'info.workspace-closed' : 'info.workspace-reopened',
                {name: this.workspace.title}))
            this.$emit('updateSystem')
        })
    }

    async updateIdea(idea: Idea, changes: any) {
        await this.saving(async () => {
            await this.$ideas.update({systemAlias: this.workspace.alias, ideaAlias: idea.alias}, changes)
            Object.assign(idea, changes)
        })
    }

    async createIdea(idea: Idea) {
        await this.saving(async () => {
            await this.$ideas.add({systemAlias: this.workspace.alias}, idea)
            this.workspace.addIdea(idea)
        })
    }

    async deleteIdea(idea: Idea) {
        await this.saving(async () => {
            await this.$ideas.delete({systemAlias: this.workspace.alias, ideaAlias: idea.alias})
            this.workspace.removeIdea(idea)
        })
    }

    async createDomain(domain: Domain) {
        await this.saving(() => this.$domains.addTo(this.workspace, domain))
    }

    async updateDomain(domain: Domain, changes: any) {
        //Pelo updateIn, e não pelo update cru: gravar e aplicar ao ambiente é uma regra só, e
        //o formulário do atributo precisa dela sem ser um componente de rota.
        await this.saving(async () => await this.$domains.updateIn(this.workspace, domain, changes))
    }

    /** Os papéis do ambiente: grava a lista e a põe no ambiente em memória, que as telas leem. */
    async saveRoles(roles: WorkspaceRole[]) {
        await this.saving(async () => {
            await this.$systems.saveRoles({systemAlias: this.workspace.alias}, roles)
            this.workspace.roles = roles
        })
    }

    async deleteDomain(domain: Domain) {
        await this.saving(async () => {
            await this.$domains.delete({systemAlias: this.workspace.alias, domainAlias: domain.alias})
            this.workspace.removeDomain(domain)
        })
    }

    async createWorkflow(workflow: Workflow) {
        await this.saving(async () => {
            await this.$workflows.add({systemAlias: this.workspace.alias}, workflow)
            this.workspace.addWorkflow(workflow)
        })
    }

    async updateWorkflow(workflow: Workflow, changes: any) {
        await this.saving(async () => {
            await this.$workflows.update({systemAlias: this.workspace.alias, workflowAlias: workflow.alias}, changes)
            Object.assign(workflow, changes)
        })
    }

    async deleteWorkflow(workflow: Workflow) {
        const workflows = this.workspace.workflows;
        await this.saving(async () => {
            await this.$workflows.delete({systemAlias: this.workspace.alias, workflowAlias: workflow.alias})
            this.workspace.removeWorkflow(workflow)
        })
    }

    /**
     * Grava o que a janela de propriedade pediu (`PropertySave`) — criar ou alterar, com o outro lado da
     * associação junto — e relê as coleções que ele tocou.
     */
    async saveProperty(save: PropertySave) {
        await this.saving(async () => {
            const where = {systemAlias: this.workspace.alias, ideaAlias: save.ideaAlias}
            if (save.propertyAlias) await this.$ideaProperties.update({...where, propertyAlias: save.propertyAlias}, save.body)
            //Sem `propertyAlias`, o corpo é a propriedade inteira — e o espelho em `otherSide`, se vai junto
            else await this.$ideaProperties.add(where, save.body as unknown as IdeaProperty)
            for (const alias of save.reload) await this.reloadProperties(this.workspace.ideaWith(alias)!)
        })
    }

    /**
     * Grava o pedido da janela ou da página de propriedade com o retorno de sempre: a mensagem de pronto,
     * ou a recusa na janela de recusa — com a frase do servidor quando ele tem uma (a fórmula que não
     * confere é a mais provável), e o genérico só quando não tem. Devolve se gravou. Um dono para os dois
     * que gravam a propriedade pelo editor: o design da coleção e a página dela.
     */
    async savePropertyReporting(save: PropertySave, idea: Idea): Promise<boolean> {
        try {
            await this.saveProperty(save)
            this.info(this.$tt(save.creating ? 'info.property-created' : 'info.property-updated',
                {property: save.name, collection: idea.name}))
            return true
        } catch (err) {
            refuse(err, idea, {fallback: this.$tt(save.creating ? 'error.create-property' : 'error.update-property',
                    {name: save.name})})
            return false
        }
    }

    async deletePropertyFromIdea(idea: Idea, property: IdeaProperty) {
        await this.saving(async () => {
            await this.$ideaProperties.delete({systemAlias: this.workspace.alias, ideaAlias: idea.alias, propertyAlias: property.alias})
            await this.reloadProperties(idea)
        })
    }

    /**
     * As propriedades da coleção como o servidor as tem agora, postas no ambiente — depois de criar,
     * alterar ou apagar uma.
     *
     * Reler, e não aplicar a mudança à mão, como era. O diálogo de propriedade trabalha numa CÓPIA, e
     * o `Object.assign` daqui mexia nela, e não na do ambiente: o modo de uso — que lê a coleção do
     * ambiente — seguia com a definição antiga até recarregar a página (relato do usuário: o default
     * novo da Prioridade não vinha ao criar um item). E há o que só o servidor sabe: o `dependsOn` das
     * calculadas, que muda com a fórmula, e sem o qual o formulário pediria a prévia pelos campos
     * errados. Uma ida ao servidor a mais por gravação de design, que é rara.
     *
     * Na MESMA coleção do ambiente (`properties` trocada, e não a coleção inteira): quem já a tem em
     * mãos — a tela de design aberta — vê a mudança sem reler nada.
     */
    private async reloadProperties(idea: Idea) {
        const {value} = await this.$ideas.get({systemAlias: this.workspace.alias, ideaAlias: idea.alias})
        idea.properties = value.properties
    }
}

export default WorkspaceRouteComponent
